import type { AlgorithmChallenge } from '@code-arcade/engine'

export const quickSort: AlgorithmChallenge = {
  id: 'quick-sort',
  title: 'Quick Sort',
  level: 3,
  chapter: 'chapter-3',
  mode: 'race-it',
  description:
    'Pick a pivot, partition smaller elements left and larger right, recurse. O(n log n) average — and in-place, so zero extra heap.',

  realWorld: {
    scenario:
      "Blink.io's flash sale triggered 2 million concurrent feed sorts. Merge sort allocated 200MB per sort node — 8GB total for sorting alone. The nodes ran out of memory mid-sale.",
    consequence:
      'Quick sort uses O(log n) stack space — about 12KB for 100,000 items instead of 200MB. The same sort, 16,000× less memory. The difference between staying up and paging to disk.',
    products: [],
  },

  visualization: {
    type: 'bar-array',
    defaultSize: 20,
    allowCustomInput: false,
  },

  interrupts: [
    {
      atStep: 25,
      character: 'barry-holt',
      mood: 'urgent',
      message: 'Memory dropped from 200MB to 12KB per node. You used the stack?',
    },
  ],

  complexity: { time: 'O(n log n) avg · O(n²) worst', space: 'O(log n)' },
  xpReward: { oneStar: 200, twoStar: 400, threeStar: 800 },

  keyTakeaway:
    "Quick sort's real lesson is the partition: in one O(n) pass, in-place, every element smaller " +
    'than the pivot lands left and every element larger lands right. That operation reappears constantly ' +
    '— quickselect uses it to find the K-th element without a full sort, database query planners use it ' +
    'to separate matching rows before aggregation, and any algorithm that needs to split a sequence into ' +
    'two groups without extra allocation is doing partition. The pivot strategy is a transferable insight ' +
    'too: randomizing it makes adversarial O(n²) statistically impossible — the same reasoning behind ' +
    'randomized hashing and reservoir sampling everywhere you need to prevent worst-case exploitation.',

  whenToUse: [
    'General-purpose in-memory sorting where cache locality matters',
    'When heap memory is constrained and O(n) auxiliary space is too expensive',
    'Sorting primitive arrays (integers, floats) — no stability needed',
    'As the outer sort in introsort (C++ std::sort, Rust slice::sort_unstable)',
  ],

  whenNotToUse: [
    'When you need a stable sort — quicksort is not stable (equal elements may reorder)',
    'Linked lists — no random access kills the cache advantage',
    'Nearly-sorted data with a bad pivot strategy — can degrade to O(n²)',
    'Adversarial input with a fixed pivot strategy — shuffle first or use median-of-three',
  ],

  pseudocode: `procedure quickSort(A, lo, hi)
  if lo ≥ hi then return
  p ← partition(A, lo, hi)
  quickSort(A, lo, p − 1)
  quickSort(A, p + 1, hi)

procedure partition(A, lo, hi)
  pivot ← A[hi]
  i ← lo − 1
  for j from lo to hi − 1
    if A[j] ≤ pivot
      i ← i + 1
      swap A[i] and A[j]
  swap A[i + 1] and A[hi]
  return i + 1`,

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static void QuickSort(int[] arr, int lo, int hi) {
    if (lo >= hi) return;
    int p = Partition(arr, lo, hi);
    QuickSort(arr, lo, p - 1);
    QuickSort(arr, p + 1, hi);
}

static int Partition(int[] arr, int lo, int hi) {
    int pivot = arr[hi], i = lo - 1;
    for (int j = lo; j < hi; j++)
        if (arr[j] <= pivot)
            (arr[++i], arr[j]) = (arr[j], arr[i]);
    (arr[i + 1], arr[hi]) = (arr[hi], arr[i + 1]);
    return i + 1;
}

// Array.Sort() uses introsort — quicksort + heapsort + insertion sort. You just saw the core:
// Array.Sort(arr);
// list.Sort();`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `def quick_sort(arr: list[int], lo: int = 0, hi: int | None = None) -> list[int]:
    if hi is None:
        hi = len(arr) - 1
    if lo < hi:
        p = _partition(arr, lo, hi)
        quick_sort(arr, lo, p - 1)
        quick_sort(arr, p + 1, hi)
    return arr

def _partition(arr: list[int], lo: int, hi: int) -> int:
    pivot, i = arr[hi], lo - 1
    for j in range(lo, hi):
        if arr[j] <= pivot:
            i += 1
            arr[i], arr[j] = arr[j], arr[i]
    arr[i + 1], arr[hi] = arr[hi], arr[i + 1]
    return i + 1


# arr.sort() uses TimSort (merge sort + insertion sort) — Python chose stability over raw speed:
# arr.sort()
# sorted_arr = sorted(arr)  # returns new list`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function quickSort(arr, lo = 0, hi = arr.length - 1) {
  if (lo < hi) {
    const p = partition(arr, lo, hi);
    quickSort(arr, lo, p - 1);
    quickSort(arr, p + 1, hi);
  }
  return arr;
}

function partition(arr, lo, hi) {
  const pivot = arr[hi];
  let i = lo - 1;
  for (let j = lo; j < hi; j++)
    if (arr[j] <= pivot) [arr[++i], arr[j]] = [arr[j], arr[i]];
  [arr[i + 1], arr[hi]] = [arr[hi], arr[i + 1]];
  return i + 1;
}

// V8 uses dual-pivot quicksort for TypedArrays, TimSort for objects — you just saw why each fits:
// arr.sort((a, b) => a - b);`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function quickSort(arr: number[], lo = 0, hi = arr.length - 1): number[] {
  if (lo < hi) {
    const p = partition(arr, lo, hi);
    quickSort(arr, lo, p - 1);
    quickSort(arr, p + 1, hi);
  }
  return arr;
}

function partition(arr: number[], lo: number, hi: number): number {
  const pivot = arr[hi];
  let i = lo - 1;
  for (let j = lo; j < hi; j++)
    if (arr[j] <= pivot) [arr[++i], arr[j]] = [arr[j], arr[i]];
  [arr[i + 1], arr[hi]] = [arr[hi], arr[i + 1]];
  return i + 1;
}

// V8 uses dual-pivot quicksort for TypedArrays, TimSort for objects — you just saw why each fits:
// arr.sort((a, b) => a - b);`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static void quickSort(int[] arr, int lo, int hi) {
    if (lo >= hi) return;
    int p = partition(arr, lo, hi);
    quickSort(arr, lo, p - 1);
    quickSort(arr, p + 1, hi);
}

static int partition(int[] arr, int lo, int hi) {
    int pivot = arr[hi], i = lo - 1;
    for (int j = lo; j < hi; j++)
        if (arr[j] <= pivot) { int t = arr[++i]; arr[i] = arr[j]; arr[j] = t; }
    int t = arr[i + 1]; arr[i + 1] = arr[hi]; arr[hi] = t;
    return i + 1;
}

// Arrays.sort() for primitives IS dual-pivot quicksort — a direct refinement of what you just built:
// Arrays.sort(arr);
// Arrays.sort(objArr, comparator);  // TimSort for objects (stable)`,
    },
  ],

  sampleDataset: {
    label: 'Flash sale cart prices ($) — 2M concurrent sorts at peak traffic',
    preview: [299, 1249, 45, 799, 199, 3499, 89, 649, 2099, 349, 99, 1849],
    atScale: {
      n: 100000,
      leftLabel: 'Merge Sort',
      leftNote: '~200MB heap alloc',
      rightLabel: 'Quick Sort',
      rightNote: '~12KB stack',
    },
  },
}
